/**
 * Nicha — format prix « 590 EUR » (montant + code devise) sur tout le site
 * S'adapte à Shopify.currency.active (changement de devise Markets)
 * BUILD: 2026-06-18-price-format-v2
 */
(function () {
  'use strict';

  const BUILD = '2026-06-18-price-format-v2';
  const ATTR = 'data-nicha-price-formatted';

  const PRICE_LEAF_SELECTORS = [
    '.product__info-wrapper .etrans-money',
    '.product__info-container .etrans-money',
    '.product__info .price .price-item--regular .etrans-money',
    '.product__info .price .price-item--sale .etrans-money',
    '.product__info .price .price-item--last .etrans-money',
    '[id*="price-template"] .etrans-money',
    '.price .price-item--regular .etrans-money',
    '.price .price-item--sale .etrans-money',
    '.price .price-item--regular',
    '.price .price-item--sale',
    '.nicha-gallery-price',
    '.cart-item__price .price-item--regular .etrans-money',
    '.cart-item__price .price-item--sale .etrans-money',
    '.cart-item__price .price-item--regular',
    '.cart-item__price .price-item--sale',
    '.totals__subtotal-value',
    '.cart__subtotal .price-item--regular',
    '.product-recommendations .price .price-item--regular .etrans-money',
    '.product-recommendations .price .price-item--sale .etrans-money',
    '.product-recommendations .price .price-item--regular',
    '.product-recommendations .price .price-item--sale',
    'product-recommendations.product-recommendations .price .price-item--regular .etrans-money',
    'product-recommendations.product-recommendations .price .price-item--regular',
    '.gp-product-price .money',
  ];

  let lastCurrency = '';

  const getCurrencyCode = () => {
    const active = window.Shopify?.currency?.active;
    if (active) return String(active).toUpperCase();
    const moneyFormat = window.theme?.moneyFormat || window.Shopify?.theme?.moneyFormat || '';
    if (moneyFormat.includes('USD') || moneyFormat.includes('$')) return 'USD';
    if (moneyFormat.includes('GBP') || moneyFormat.includes('£')) return 'GBP';
    return 'EUR';
  };

  const isAlreadyFormatted = (text, currencyCode) => {
    const t = String(text || '').trim();
    return new RegExp(`^\\d+(?:[.,]\\d{1,2})?\\s+${currencyCode}$`).test(t);
  };

  const parseAmount = (text) => {
    if (!text) return null;
    const raw = String(text).trim();
    if (!raw || raw.includes('{{')) return null;
    if (isAlreadyFormatted(raw, getCurrencyCode())) {
      return parseFloat(raw.split(/\s+/)[0].replace(',', '.'));
    }

    let numStr = raw.replace(/[^\d,.']/g, '');
    if (!numStr) return null;

    if (numStr.includes(',') && numStr.includes('.')) {
      if (numStr.lastIndexOf(',') > numStr.lastIndexOf('.')) {
        numStr = numStr.replace(/\./g, '').replace(',', '.');
      } else {
        numStr = numStr.replace(/,/g, '');
      }
    } else if (numStr.includes(',')) {
      const parts = numStr.split(',');
      numStr = parts.length === 2 && parts[1].length <= 2 ? parts.join('.') : parts.join('');
    }

    const value = parseFloat(numStr);
    return Number.isFinite(value) ? value : null;
  };

  const formatAmount = (amount, currencyCode) => {
    const rounded = Math.round(amount * 100) / 100;
    const whole = Math.abs(rounded - Math.round(rounded)) < 0.001;
    const display = whole ? String(Math.round(rounded)) : rounded.toFixed(2);
    return `${display} ${currencyCode}`;
  };

  const getPriceLeaf = (el) => {
    if (!el || el.closest('.visually-hidden')) return null;
    const nested = el.querySelector(':scope > .etrans-money, :scope > .money');
    if (nested && nested.textContent.trim() && !nested.textContent.includes('{{')) return nested;
    if (el.matches('.etrans-money, .money, .price-item, .nicha-gallery-price')) return el;
    return el.childElementCount === 0 ? el : null;
  };

  const formatLeaf = (leaf) => {
    const currency = getCurrencyCode();
    const text = (leaf.textContent || '').trim();
    if (!text || text.includes('{{')) return;

    const amount = parseAmount(text);
    if (amount == null) return;

    const formatted = formatAmount(amount, currency);
    if (text !== formatted) {
      leaf.textContent = formatted;
    }
    leaf.setAttribute(ATTR, currency);
  };

  const clearFormatted = () => {
    document.querySelectorAll(`[${ATTR}]`).forEach((el) => el.removeAttribute(ATTR));
  };

  const scan = () => {
    const currency = getCurrencyCode();
    if (currency !== lastCurrency) {
      clearFormatted();
      lastCurrency = currency;
    }

    const seen = new Set();
    PRICE_LEAF_SELECTORS.forEach((selector) => {
      document.querySelectorAll(selector).forEach((el) => {
        const leaf = getPriceLeaf(el);
        if (!leaf || seen.has(leaf)) return;
        seen.add(leaf);
        formatLeaf(leaf);
      });
    });
  };

  const init = () => {
    lastCurrency = getCurrencyCode();
    scan();
    [300, 800, 1500, 3000, 5000, 8000].forEach((delay) => setTimeout(scan, delay));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', () => setTimeout(scan, 100));
  document.addEventListener('variant:change', () => setTimeout(scan, 80));

  if (!window.__NICHA_PRICE_FORMAT_OBSERVER__) {
    window.__NICHA_PRICE_FORMAT_OBSERVER__ = true;
    let scheduled = null;
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        const target = mutation.target;
        if (
          target?.nodeType === Node.TEXT_NODE ||
          target?.matches?.('.etrans-money, .money, .price-item, .price-item--regular, .price-item--sale') ||
          target?.closest?.('.price, .product__info-wrapper, .product__info-container, .cart-item__price')
        ) {
          if (scheduled) return;
          scheduled = setTimeout(() => {
            scheduled = null;
            scan();
          }, 80);
          return;
        }
      }
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) setTimeout(scan, 100);
  });

  window.NichaPriceFormat = { build: BUILD, refresh: scan, getCurrencyCode };
})();
